import { GLManager } from '@play-co/timestep-core/renderer';
import {
  ButtonView,
  CustomFilter,
  ImageScaleView,
  View,
  type ViewOpts,
} from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import { HueShift, getHueShift } from 'src/game/cards/shaders/HueShift';
import {
  dreamDriftGetHeroColor,
  dreamDriftWearPet,
} from 'src/game/dreamDrift/dreamDrift.costumes';
import { createUIPet } from 'src/game/pet/pet.utils';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';

/** Presentation data associated with one selectable Dream Drift hero. */
type DreamDriftHeroCardConfig = {
  banner: string;
};

/** Hero-specific artwork and localization used by the selection cards. */
const HERO_CARD_CONFIG: Record<DreamDriftHero, DreamDriftHeroCardConfig> = {
  rumbleHero: {
    banner: 'assets/ui/dreamDrift/popupHeroSelection/banner_rumble.png',
  },
  whirlwindDancer: {
    banner: 'assets/ui/dreamDrift/popupHeroSelection/banner_dancer.png',
  },
  skySummoner: {
    banner: 'assets/ui/dreamDrift/popupHeroSelection/banner_summoner.png',
  },
  prismPainter: {
    banner: 'assets/ui/dreamDrift/popupHeroSelection/banner_painter.png',
  },
};

/** Presentation supported by a Dream Drift hero card. */
type DreamDriftHeroSelectionCardPresentation = 'selection' | 'summary';

/** Options required to create one Dream Drift hero card. */
type DreamDriftHeroSelectionCardOpts = Omit<ViewOpts, 'height' | 'width'> & {
  hero: DreamDriftHero;
  onSelect?: (hero: DreamDriftHero) => void;
  presentation?: DreamDriftHeroSelectionCardPresentation;
};

/** Create the hue-shift filter that colors one hero circle to match its banner. */
function createHeroCircleFilter(
  hero: DreamDriftHero,
): CustomFilter | undefined {
  // Renderers without custom shader support retain the source red circle.
  if (!GLManager.isSupported) return undefined;

  // Shift the shared red artwork toward the configured hero color.
  const hueShift = GLManager.createShader(HueShift);
  hueShift.shift = getHueShift('#F33C38', dreamDriftGetHeroColor(hero));

  // Apply the configured shader through the standard Timestep filter wrapper.
  return new CustomFilter(hueShift);
}

/** Animated hero portrait used by selection and pre-game confirmation popups. */
export default class DreamDriftHeroSelectionCard extends View {
  /** Hero currently represented by this reusable card. */
  private hero: DreamDriftHero;

  /** Shared portrait circle recolored whenever the represented hero changes. */
  private readonly circle: ImageScaleView;

  /** Hero-specific name banner updated with the represented hero. */
  private readonly banner: ImageScaleView;

  /** Localized hero name rendered inside the hero-specific banner. */
  private readonly heroName: LangBitmapFontTextShadowView;

  /** Optional power description shown by full selection cards. */
  private readonly description?: LangBitmapFontTextShadowView;

  /** Badge shown only while this card is the active hero selection. */
  private readonly selectionBadge?: ImageScaleView;

  /** Dressed pet whose animation follows this card's selected state. */
  private readonly pet: ReturnType<typeof createUIPet>;

  /**
   * Create one hero card using the player's happy pet and hero-specific chrome.
   *
   * @param opts Hero identity, presentation, position, and optional selection callback.
   */
  constructor({
    hero,
    onSelect,
    presentation = 'selection',
    ...opts
  }: DreamDriftHeroSelectionCardOpts) {
    // Use the shorter summary shape when the card only confirms the active hero.
    const isSelectionCard = presentation === 'selection';

    // Create the fixed-size card used by selection and pre-game popups.
    super({
      ...opts,
      width: 340,
      height: isSelectionCard ? 420 : 260,
    });

    // Retain the represented hero for later card reuse and selection callbacks.
    this.hero = hero;
    const centerX = this.style.width / 2;
    const circleSize = downscale(465);

    // Draw the shared circle above the pet and tint it for this hero.
    this.circle = new ImageScaleView({
      superview: this,
      image: 'assets/ui/dreamDrift/popupHeroSelection/hero_circle.png',
      width: downscale(465),
      height: downscale(465),
      x: centerX,
      y: circleSize / 2,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      filter: createHeroCircleFilter(hero),
      zIndex: 1,
    });

    // Repeat the player's current pet in the requested happy state.
    this.pet = createUIPet('happy');
    dreamDriftWearPet(this.pet, hero);
    this.pet.updateOpts({
      superview: this.circle,
      x: circleSize / 2,
      y: circleSize / 2 + 30,
      scale: 0.8,
    });

    // Overlay the hero-specific banner across the portrait circle.
    this.banner = new ImageScaleView({
      superview: this,
      image: HERO_CARD_CONFIG[hero].banner,
      width: downscale(426),
      height: downscale(108),
      x: centerX,
      y: circleSize - 1,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      zIndex: 3,
    });

    // Center the localized hero name inside its colored banner.
    this.heroName = new LangBitmapFontTextShadowView({
      superview: this.banner,
      x: this.banner.style.width / 2,
      y: this.banner.style.height / 2 - 6,
      width: this.banner.style.width - 50,
      height: this.banner.style.height - 8,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      color: '#FFFFFF',
      font: 'Body',
      size: 26,
      textShadowColor: '#70272D',
      textShadowOffsetX: 0,
      textShadowOffsetY: 3,
      localeText: () => i18n(`dreamDrift.heroSelection.heroes.${hero}.name`),
    });

    // Summary cards end after the animated portrait and hero-name banner.
    if (!isSelectionCard) return;

    // Place the numberless power description directly below the banner.
    this.description = new LangBitmapFontTextShadowView({
      superview: this,
      x: centerX,
      y: circleSize + 70,
      width: this.style.width - 18,
      height: 74,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'top',
      color: '#FFFFFF',
      font: 'Body',
      size: 20,
      wordWrap: true,
      textShadowColor: '#31162F',
      textShadowOffsetX: 0,
      textShadowOffsetY: 3,
      localeText: () =>
        i18n(`dreamDrift.heroSelection.heroes.${hero}.description`),
    });

    // Keep the selected-state badge above every other card element.
    this.selectionBadge = new ImageScaleView({
      superview: this,
      image: 'assets/ui/dreamDrift/popupHeroSelection/icon_select.png',
      width: downscale(218),
      height: downscale(218),
      x: circleSize - 6,
      y: 55,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      visible: false,
      zIndex: 4,
    });

    // Cover the complete card with one transparent selection target.
    new ButtonView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      zIndex: 5,
      onClick: () => {
        // Give immediate feedback before applying the new popup selection.
        sounds.sfx.play('ok');
        onSelect?.(this.hero);
      },
    });
  }

  /**
   * Reconfigure this card to display another Dream Drift hero.
   *
   * @param hero Hero whose color, costume, banner, and copy should be displayed.
   */
  public setHero(hero: DreamDriftHero): void {
    // Retain the hero returned by any subsequent selection callback.
    this.hero = hero;

    // Recolor the shared portrait circle for the new hero identity.
    this.circle.updateOpts({ filter: createHeroCircleFilter(hero) });

    // Replace every costume slot on the existing animated pet.
    dreamDriftWearPet(this.pet, hero);

    // Replace the hero-specific banner without rebuilding its view hierarchy.
    this.banner.setImage(HERO_CARD_CONFIG[hero].banner);

    // Refresh the localized hero name and optional full-card description.
    this.heroName.localeText = () =>
      i18n(`dreamDrift.heroSelection.heroes.${hero}.name`);
    if (this.description) {
      this.description.localeText = () =>
        i18n(`dreamDrift.heroSelection.heroes.${hero}.description`);
    }
  }

  /**
   * Synchronize this card's badge and animation with the active hero.
   *
   * @param selected Whether this card represents the current selection.
   */
  public setSelected(selected: boolean): void {
    // Show exactly one badge while a hero is selected.
    this.selectionBadge?.updateOpts({ visible: selected });

    // Keep the active hero moving while every other card remains static.
    if (selected) {
      this.pet.petClip.resume();
      return;
    }

    // Freeze unselected heroes on their current animation frame.
    this.pet.petClip.pause();
  }
}
